import React from 'react';
import { BaseTableProps } from '../types';
import IndeterminateCheckbox from '../IndeterminateCheckbox';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import { useTableClientState } from './useTableClientState';

export const useClientControlledTableConfig = <T,>({
  data,
  columns: originColumns,
  sortingState,
  filteringState,
  rowSelectionState,
  paginationState,
  columnPinning,
  rowCount,
}: Omit<BaseTableProps<T>, 'emptyText'>): { table: Table<T>; pagination: PaginationState } => {
  const { sorting, setSorting, filtering, setFiltering, rowSelection, setRowSelection, pagination, setPagination } =
    useTableClientState({
      sortingState,
      filteringState,
      rowSelectionState,
      paginationState,
    });

  const table = useReactTable({
    data,
    columns: rowSelectionState
      ? [
          {
            id: 'selection',
            header: ({ table }): JSX.Element => (
              <IndeterminateCheckbox
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
              />
            ),
            cell: ({ row }): JSX.Element => (
              <IndeterminateCheckbox
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                indeterminate={row.getIsSomeSelected()}
                onChange={row.getToggleSelectedHandler()}
              />
            ),
            size: 50,
            enableColumnFilter: false,
          },
          ...originColumns,
        ]
      : originColumns,
    state: {
      sorting,
      columnFilters: filtering,
      rowSelection,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setFiltering,
    onPaginationChange: setPagination,
    rowCount,
    initialState: {
      sorting,
      columnPinning: {
        left: ['selection', ...columnPinning],
      },
    },
  });

  return { table, pagination };
};
